import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "theo.liveDebug";
const CHANGE_EVENT = "theo-live-debug-change";

function readLiveDebug(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The live-debug sidebar switch, persisted in localStorage.
 *
 * Every caller sees the same value: the audit popup turns it on, the panel turns it off, and
 * both chat surfaces read it, so a change in one is broadcast to the rest (and to other tabs).
 */
export function useLiveDebug(): [boolean, (enabled: boolean) => void] {
  const [enabled, setEnabledState] = useState<boolean>(readLiveDebug);

  useEffect(() => {
    const sync = () => setEnabledState(readLiveDebug());
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) sync();
    };
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    try {
      if (next) window.localStorage.setItem(STORAGE_KEY, "1");
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage blocked: still flip it for this page view.
    }
    setEnabledState(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [enabled, setEnabled];
}
